import React, { useEffect, useState } from "react";
import { useLocation, useSearch } from "wouter";
import { BriefcaseBusiness, Building2, Globe2, Loader2, Mail, Moon, Sun, UserRound } from "lucide-react";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ContainedBadge } from "@/components/ContainedBadge";
import { RouteFeedback } from "@/components/RouteFeedback";

const LOGO_URL = "https://d2xsxph8kpxj0f.cloudfront.net/98466279/N8L9TKRXjtLMpzosC4Ao5k/mizano-icon-exact-192_e336f133.png";

type Choice = "individual" | "business";

export default function AccountType() {
  const { user, loading } = useAuth();
  const userId = user?.id;
  const { language, toggleLanguage, isRTL, t } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const [, setLocation] = useLocation();
  const search = useSearch();
  const utils = trpc.useUtils();
  const { data: status, isLoading } = trpc.accountType.status.useQuery(undefined, { enabled: !!userId });
  const [choice, setChoice] = useState<Choice | null>(() => typeof window !== "undefined" && new URLSearchParams(window.location.search).get("workspace") === "business" ? "business" : null);
  const [companyName, setCompanyName] = useState("");
  const [companyNameAr, setCompanyNameAr] = useState("");
  const [crNumber, setCrNumber] = useState("");
  const [fiscalMonth, setFiscalMonth] = useState("12");
  const [fiscalDay, setFiscalDay] = useState("31");
  const requestedBusinessWorkspace = new URLSearchParams(search).get("workspace") === "business";
  const businessApproved = status?.businessModeEnabled === true;
  const accessStatusResolved = !isLoading && status !== undefined;
  const deniedBusinessWorkspaceRequest = requestedBusinessWorkspace && accessStatusResolved && !businessApproved;

  useEffect(() => {
    if (!loading && !userId) setLocation("/signin", { replace: true });
  }, [loading, setLocation, userId]);

  useEffect(() => {
    if (!deniedBusinessWorkspaceRequest) return;
    setChoice((current) => current === "individual" ? current : "individual");
    setLocation("/account-type", { replace: true });
  }, [deniedBusinessWorkspaceRequest, setLocation]);

  useEffect(() => {
    if (status?.accountTypeCompleted) setLocation("/dashboard", { replace: true });
  }, [setLocation, status?.accountTypeCompleted]);

  const selectAccountType = trpc.accountType.select.useMutation({
    onSuccess: async () => {
      await Promise.all([utils.accountType.status.invalidate(), utils.profile.get.invalidate(), utils.auth.me.invalidate()]);
      setLocation("/dashboard", { replace: true });
    },
  });

  const requestedSelection = choice ?? (status?.accountTypeCompleted ? status.accountType : null);
  const selected = requestedSelection === "business" && !businessApproved ? "individual" : requestedSelection;
  const resolvedCompanyName = companyName || status?.companyName || "";
  const canContinue = selected === "individual" || (selected === "business" && Boolean(resolvedCompanyName.trim()));
  const heading = t("accountType.title");

  const submit = () => {
    if (!selected) return;
    selectAccountType.mutate({
      accountType: selected,
      email: status?.email?.trim() || undefined,
      companyName: resolvedCompanyName.trim() || undefined,
      companyNameAr: companyNameAr.trim() || undefined,
      crNumber: crNumber.trim() || undefined,
      fiscalYearEndMonth: Number(fiscalMonth),
      fiscalYearEndDay: Number(fiscalDay),
    });
  };

  if (loading || isLoading || !user || deniedBusinessWorkspaceRequest) return <div className="min-h-screen min-h-[100dvh] bg-background"><RouteFeedback isLoading onRetry={() => window.location.reload()} /></div>;
  if (status === undefined) return <div className="min-h-screen min-h-[100dvh] bg-background"><RouteFeedback isLoading={false} isError onRetry={() => window.location.reload()} /></div>;

  return (
    <div className="min-h-[100dvh] bg-background text-foreground safe-area-top safe-area-bottom" dir={isRTL ? "rtl" : "ltr"}>
      <header className="border-b border-border bg-background/95 backdrop-blur">
        <div className="mx-auto flex min-h-16 max-w-5xl items-center gap-3 px-4 sm:px-6">
          <button onClick={() => setLocation("/")} className="tap-target flex items-center gap-2 text-start" aria-label={t("accountType.backHome")}>
            <img src={LOGO_URL} alt="" className="h-8 w-8 rounded-lg" />
            <span className="font-display text-base font-semibold">Mizano</span>
          </button>
          <div className="ms-auto flex items-center gap-1">
            <Button variant="ghost" size="icon" className="min-h-11 min-w-11" onClick={() => toggleTheme?.()} aria-label={theme === "dark" ? t("accountType.lightMode") : t("accountType.darkMode")}>
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <Button variant="ghost" size="icon" className="min-h-11 min-w-11" onClick={toggleLanguage} aria-label={t("accountType.toggleLanguage")}>
              <Globe2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-5xl px-4 py-8 pb-[calc(2rem+env(safe-area-inset-bottom))] sm:px-6 sm:py-12">
        <section className="w-full" data-testid="account-type-flow">
          <div className="mx-auto mb-7 max-w-2xl text-center">
            <p className="font-mono text-xs uppercase tracking-[.14em] text-muted-foreground">{t("accountType.eyebrow")}</p>
            <h1 className="mt-3 font-display text-3xl font-medium tracking-tight sm:text-4xl">{heading}</h1>
            <p className="mt-3 text-muted-foreground">{t("accountType.description")}</p>
          </div>

          <div className="mx-auto grid max-w-4xl gap-4 sm:grid-cols-2">
            <AccountTypeOption icon={<UserRound />} title={t("accountType.individualTitle")} description={t("accountType.individualDescription")} selected={selected === "individual"} onClick={() => setChoice("individual")} />
            <AccountTypeOption icon={<BriefcaseBusiness />} title={t("accountType.businessTitle")} description={t("accountType.businessDescription")} selected={selected === "business"} onClick={() => setChoice("business")} disabled={!businessApproved} badge={!businessApproved ? t("workspace.inDevelopment") : undefined} />
          </div>

          {selected && <Card className="mx-auto mt-5 max-w-2xl border-border bg-card text-card-foreground">
            <CardHeader>
              <CardTitle>{selected === "business" ? t("accountType.businessDetails") : t("accountType.continueTitle")}</CardTitle>
              <CardDescription>{selected === "business" ? t("accountType.businessDetailsDescription") : t("accountType.continueDescription")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {selected === "business" && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <FormField id="company-name" label={t("accountType.companyName")} icon={<Building2 className="h-4 w-4" />}><Input id="company-name" defaultValue={status?.companyName ?? ""} onChange={(event) => setCompanyName(event.target.value)} className="min-h-11" /></FormField>
                  <FormField id="company-name-ar" label={t("accountType.companyNameAr")}><Input id="company-name-ar" value={companyNameAr} onChange={(event) => setCompanyNameAr(event.target.value)} className="min-h-11" /></FormField>
                  <FormField id="cr-number" label={t("accountType.crNumber")}><Input id="cr-number" value={crNumber} onChange={(event) => setCrNumber(event.target.value)} className="min-h-11" /></FormField>
                  <div className="grid grid-cols-2 gap-3"><FormField id="fiscal-month" label={t("accountType.fiscalMonth")}><Input id="fiscal-month" type="number" min="1" max="12" value={fiscalMonth} onChange={(event) => setFiscalMonth(event.target.value)} className="min-h-11" /></FormField><FormField id="fiscal-day" label={t("accountType.fiscalDay")}><Input id="fiscal-day" type="number" min="1" max="31" value={fiscalDay} onChange={(event) => setFiscalDay(event.target.value)} className="min-h-11" /></FormField></div>
                </div>
              )}
              {selectAccountType.error && <p className="text-sm text-destructive">{t("accountType.submitError")}</p>}
              <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
                <Button type="button" variant="ghost" className="min-h-11" onClick={() => { setChoice(null); setCompanyName(""); }} disabled={selectAccountType.isPending}>{t("accountType.back")}</Button>
                <Button type="button" className="min-h-12 w-full bg-primary text-primary-foreground sm:w-auto" disabled={!canContinue || selectAccountType.isPending} onClick={submit}>{selectAccountType.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : t("accountType.continue")}</Button>
              </div>
            </CardContent>
          </Card>}
        </section>
      </main>
    </div>
  );
}

function AccountTypeOption({ icon, title, description, selected, onClick, disabled = false, badge }: { icon: React.ReactNode; title: string; description: string; selected: boolean; onClick: () => void; disabled?: boolean; badge?: string }) {
  return <button type="button" onClick={onClick} disabled={disabled} className={`min-h-44 rounded-2xl border p-5 text-start transition-colors ${disabled ? "cursor-not-allowed border-border bg-muted/40 text-muted-foreground opacity-65" : selected ? "border-primary bg-primary/10 ring-1 ring-primary" : "border-border bg-card hover:border-primary/60"}`} aria-pressed={selected} aria-disabled={disabled}><span className={`mb-5 inline-flex h-11 w-11 items-center justify-center rounded-xl ${selected && !disabled ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>{icon}</span><span className="flex min-w-0 flex-wrap items-center gap-2"><span className="block text-lg font-semibold">{title}</span>{badge ? <ContainedBadge>{badge}</ContainedBadge> : null}</span><span className="mt-2 block text-sm leading-6 text-muted-foreground">{description}</span></button>;
}

function FormField({ id, label, icon, children }: { id: string; label: string; icon?: React.ReactNode; children: React.ReactNode }) {
  return <div className="space-y-2"><Label htmlFor={id} className="flex items-center gap-2">{icon}{label}</Label>{children}</div>;
}
